import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { CheckMark } from '@lyearn/icons';
import { ArrowIosDownward } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Loader, Stack, Typography } from '@lyearn/ui';
import AutocompleteSearch from '@/components/AutocompleteSearch';

import { autocompleteClasses, popperStyles } from './styles';
import { AutoCompleteDateTag, useDateTagAutoComplete } from './useDateTagAutoComplete';

interface DataTagsAutoCompleteProps {
  selectedTagId?: string;
  onChange: (tagId?: string) => void;
  disabled?: boolean;
}

const renderAutoCompleteOption =
  (selectedValue?: AutoCompleteDateTag) => (val: AutoCompleteDateTag) => {
    const isSelected = selectedValue?.id === val.id;

    return (
      <Stack alignItems="center" className="mb-2 w-full py-10 px-12" justifyContent="between">
        <Typography
          className="truncate"
          color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
          variant="body-short-01">
          {val.name}
        </Typography>
        {isSelected ? (
          <CheckMark className="h-20 w-20 flex-shrink-0 ms-12" fillColorToken="icon-active" />
        ) : null}
      </Stack>
    );
  };

function DateTagsAutoComplete({ selectedTagId, onChange, disabled }: DataTagsAutoCompleteProps) {
  const { t } = useTranslation();
  const { tags, onInputChange, isLoading, fetchMore, selectedTag, fetchingSelected } =
    useDateTagAutoComplete(t, selectedTagId);
  const loading = isLoading || fetchingSelected;

  if (loading) {
    return (
      <Stack
        alignItems="center"
        className={cn(popperStyles, 'rounded-8 border border-interactive-secondary-hover px-16')}
        justifyContent="start">
        <Loader />
        <Typography className="ms-8" color="text-text-placeholder" variant="body-short-01">
          {t('Group:Editor.SelectProperty')}
        </Typography>
      </Stack>
    );
  }

  return (
    <AutocompleteSearch
      disableClearable
      className="flex-1"
      classes={autocompleteClasses}
      closeIcon={null}
      data={tags}
      data-id="tag-and-properties"
      disabled={disabled || loading}
      fetchMore={fetchMore}
      initialValue={selectedTag?.name}
      placeholder={t('Group:Editor.SelectProperty')}
      popupIcon={<ArrowIosDownward className="h-20 w-20" fillColorToken="icon-placeholder" />}
      renderers={{
        renderOption: renderAutoCompleteOption(selectedTag),
      }}
      size="large"
      onChange={onChange}
      onInputChange={onInputChange}
    />
  );
}

export default DateTagsAutoComplete;
